/* eslint-disable max-lines */
import { isEmpty, isObject } from './objects';
import { formatPrice } from './numbers';
import {
    ZERO_ROW_HEIGHT,
    ONE_ROW_HEIGHT,
    ATTR_NOT_TO_FORMAT,
    ATTR_WITHOUT_SUFFIX,
    PLUS_SUFFIX,
    QUICK_FILTERS
} from 'Constants/filters';
import { visualizationFilters, selectionType } from 'Constants/filters';
import { viewTypeTanakConfig } from 'Selectors/viewTypes'; // todo: move this to helpers

export const getValueFromAppliedFilter = obj => {
    let final = [];

    Object.keys(obj).forEach(key => {
        const value = obj[key];

        if (value.min || value.max) {
            final = { ...final };
            if (value.min) {
                final.min = value.min;
            }
            if (value.max) {
                final.max = value.max;
            }
        }
        else if (value.id) {
            final.push(value.id);
        }
    });
    return final;
};

/**
 *
 * @param {*} applied - The Filter Objects { price: { id: "price", values={max: 1000, in: 500 }}}
 */
export const parseAppliedFilters = applied => {
    return Object.keys(applied).reduce((curr, key) => {
        const value = applied[key];
        const val = value.values || value.value;
        const appliedFilterValue = getValueFromAppliedFilter(val);
        const appliedObj = { ...curr };

        if (appliedFilterValue) {
            appliedObj[key] = appliedFilterValue;
        }

        return appliedObj;
    }, {});
};

/**
 * filter out searched values
 * @param {string} searchVal
 * @param {Array.<string>} values - existing ids
 * @param {object} valuesInfo - values info map e.g. { 'audi': { 'value': 'audi', 'name': 'Audi'}}
 * @returns {object} array of brand/model ids
 */
export const getSearchedValues = (searchVal, values, valuesInfo) => {
    return (typeof searchVal === 'string') ? values.filter(id => {
        return valuesInfo[id].name.toLowerCase().includes(searchVal.toLowerCase());
    }) : values;
};

export const sortByName = (arr = [], info, keyName) => {
    return arr.sort((a, b) => {
        const infoA = a && info[a] ? info[a] : {};
        const infoB = b && info[b] ? info[b] : {};
        const nameA = (infoA[keyName] || '').toUpperCase(); // ignore upper and lowercase
        const nameB = (infoB[keyName] || '').toUpperCase(); // ignore upper and lowercase

        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }

        // names must be equal
        return 0;
    });
};

/**
 * get reconstructed render info of the filter
 * @param {Array.<string>} values - existing ids
 * @param {object} valuesInfo - values info map e.g. { 'audi': { 'value': 'audi', 'name': 'Audi'}}
 * @param {object} restCustomConfig
 * @param {object} restRender
 * @returns {object} array of brand/model ids
 */
export const getTransformedRender = (values, valuesInfo, restCustomConfig, restRender) => {
    return {
        render: {
            customConfiguration: {
                values,
                valuesInfo,
                ...restCustomConfig
            },
            ...restRender
        }
    };
};

/**
 * get applied values of filter
 * @param {object} transformValues - used for storage of nested values
 * @param {object} appliedFilters - Map of applied filter values e.g. { 'brand': { 'values': []}}
 * @param {string} currFilterAttr - current filter attribute is the uid
 * @param {Array.<string>} values - existing ids
 * @returns {Array.<string>} array of brand/model ids
 */
export const getAppliedValues = (transformValues, appliedFilters, currFilterAttr, values) => {
    let iterValues = [];

    if ((transformValues[currFilterAttr] && transformValues[currFilterAttr].values)) {
        iterValues = iterValues.concat(transformValues[currFilterAttr].values);
    }

    if (appliedFilters && appliedFilters[currFilterAttr]) {
        iterValues = appliedFilters[currFilterAttr].reduce((arr, val) => {
            if (arr.indexOf(val) === -1) {
                arr.push(val);
            }

            return arr;
        }, iterValues);
    }

    return iterValues.length > 0 ? iterValues : values;
};

/**
 * Get nested values of the parent filter
 * @param {object} transformValues - used for storage of nested values
 * @param {object} appliedFilters - Map of applied filter values e.g. { 'brand': { 'values': []}}
 * @param {string} currFilterAttr - current filter attribute is the uid
 * @param {Array.<string>} values - existing ids
 * @param {object} valuesInfo - values info map e.g. { 'audi': { 'value': 'audi', 'name': 'Audi'}}
 * @returns {Array.<string>} array of brand/model ids
 */
export const getNestedAppliedValues = (
    transformValues, appliedFilters, currFilterAttr, values, valuesInfo
) => {
    const newValues = [];
    const iterValues = getAppliedValues(transformValues, appliedFilters, currFilterAttr, values);

    iterValues.forEach(id => {
        const info = valuesInfo[id];

        if (info && info.nestedValues) {
            newValues.push(...info.nestedValues);
        }
    });

    return newValues;
};

/**
 * Get filtered or applied values array
 * @param {object} appliedFilters - Map of applied filter values e.g. { 'brand': { 'values': []}}
 * @param {string} searchVal
 * @param {string} currFilterAttr - current filter attribute is the uid
 * @param {object} transformValues - used for storage of nested values
 * @param {Array.<string>} values - existing ids
 * @param {object} valuesInfo - values info map e.g. { 'audi': { 'value': 'audi', 'name': 'Audi'}}
 * @returns {Array.<string>} array of brand/model ids
 */
export const getValues = ({
    appliedFilters,
    searchVal,
    currFilterAttr,
    transformValues,
    values,
    valuesInfo,
    isChild = false
}) => {
    if (searchVal) {
        const searchedValues = getSearchedValues(searchVal, values, valuesInfo);

        return isChild ? sortByName(searchedValues, valuesInfo, 'name') : searchedValues;
    }
    else if (appliedFilters) {
        const appliedValues = getAppliedValues(transformValues, appliedFilters, currFilterAttr, values);

        return isChild ? sortByName(appliedValues, valuesInfo, 'name') : appliedValues;
    }

    return values;
};

const isFilterOfNestedType = ({ nestedFilterAttr, parentFilterAttr }) => nestedFilterAttr || parentFilterAttr;

/**
 * Transform Nested Select Filter values on basis of searchString or selectedValues
 * @param {object} nestedSelectfilterInfo - nested filter info obj e.g. brand
 * @param {object} filtersInfo - Map of filters e.g { 'brand': {*}}
 * @param {string} searchVal
 * @param {object} appliedFilters - Map of applied filter values e.g. { 'brand': { values: []}}
 * @returns {object} A map of filtersInfo e.g { 'brand': {*}}
 */
export const getFilteredNestedSelect = (nestedSelectfilterInfo, filtersInfo, searchVal, appliedFilters) => {
    if (!isFilterOfNestedType(nestedSelectfilterInfo) && isEmpty(filtersInfo)) {
        return false;
    }

    if (!searchVal && isEmpty(appliedFilters)) {
        return filtersInfo;
    }

    const transformedFilterInfo = {};
    const transformValues = {};

    const transformFilter = ({ nestedFilterAttr, render = {}, ...currFilter }) => {
        const { customConfiguration: { values = [], valuesInfo = {}, ...restCustomConfig } = {}, ...restRender } = render;

        if (nestedFilterAttr) {
            // only applied in case appliedFilters
            if (!transformValues[nestedFilterAttr] && appliedFilters && !searchVal) {
                transformValues[nestedFilterAttr] = { values: [] };
                transformValues[nestedFilterAttr].values = transformValues[nestedFilterAttr].values.concat(getNestedAppliedValues(
                    transformValues, appliedFilters, currFilter.attribute, values, valuesInfo
                ));
            }

            transformedFilterInfo[currFilter.attribute] = {
                ...currFilter,
                nestedFilterAttr,
                ...getTransformedRender(
                    getValues({
                        appliedFilters: null,
                        searchVal,
                        currFilterAttr: currFilter.attribute,
                        transformValues,
                        values,
                        valuesInfo
                    }),
                    valuesInfo,
                    restCustomConfig,
                    restRender
                )
            };
            transformFilter(filtersInfo[nestedFilterAttr]);
        }
        else {
            transformedFilterInfo[currFilter.attribute] = {
                ...currFilter,
                ...getTransformedRender(
                    // change
                    getValues({
                        appliedFilters,
                        searchVal,
                        currFilterAttr: currFilter.attribute,
                        transformValues,
                        values,
                        valuesInfo,
                        isChild: true
                    }),
                    valuesInfo,
                    restCustomConfig,
                    restRender
                )
            };
        }
    };

    transformFilter(nestedSelectfilterInfo);

    return transformedFilterInfo;
};

/**
 * Tranform nestedValues of brand/model to values(Array) & valuesInfo(Map)
 * @param {Array.<object>} values
 * @param {Object} parentCustomConfig
 * @returns {object} A map of filtersInfo e.g { 'brand': {*}}
 */
export const transformNestedValues = (values, parentCustomConfig) => {
    if (!values) {
        return {};
    }
    const tranformedValuesInfo = {};
    const tranformedValues = [];

    // performing map and reduce to generate tranformedValues and tranformedValuesInfo respectively
    values.forEach(filterValue => {
        tranformedValuesInfo[filterValue.value] = {
            ...filterValue
        };
        tranformedValues.push(filterValue.value);
    });

    if (parentCustomConfig) {
        parentCustomConfig.values.forEach(parentId => {
            const { nestedValues: parentNestedValues, name: parentName } = parentCustomConfig.valuesInfo[parentId];

            if (parentNestedValues) {
                parentNestedValues.forEach(childId => {
                    if (tranformedValuesInfo[childId]) {
                        tranformedValuesInfo[childId].name = parentName ? `${parentName} ${tranformedValuesInfo[childId].name}` : tranformedValuesInfo[childId].name;
                    }
                });
            }
        });
    }
    return {
        valuesInfo: tranformedValuesInfo,
        values: sortByName(tranformedValues, tranformedValuesInfo, 'name')
    };
};

/**
 * Transform Nested Select Filter id to attr and values to values(Array) & valuesInfo(Map)
 * @param {object} nestedSelectfilterInfo - nested filter info obj e.g. brand
 * @param {object} filtersInfo - Map of filters e.g { 'brand': {*}}
 * @param {string} filterIdToAttrMap - Map of filters id to attribute e.g { '1': 'brand'
 * @returns {object} A map of filtersInfo e.g { 'brand': {*}}
 */
export const transformNestedSelect = (nestedSelectfilterInfo, filterIdToAttrMap, filtersInfo) => {
    const transformedFilterInfo = {};
    const transformFilter = ({ nestedFilterId, render = {}, ...currFilter }, parentFilterInfo) => {
        const { customConfiguration: { values = [], ...restCustomConfig } = {}, ...restRender } = render;
        const transformedRender = {
            render: {
                customConfiguration: {
                    ...transformNestedValues(
                        values,
                        parentFilterInfo ? parentFilterInfo.render.customConfiguration : null
                    ), // todo: add safety check
                    ...restCustomConfig
                },
                ...restRender
            }
        };

        const parentFilter = parentFilterInfo ? { parentFilterAttr: parentFilterInfo.attribute } : {};

        if (nestedFilterId) {
            const nestedFilterAttr = filterIdToAttrMap[nestedFilterId];

            transformedFilterInfo[currFilter.attribute] = {
                ...currFilter,
                nestedFilterAttr,
                ...parentFilter,
                ...transformedRender
            };
            transformFilter(filtersInfo[nestedFilterAttr], transformedFilterInfo[currFilter.attribute]);
        }
        else {
            transformedFilterInfo[currFilter.attribute] = { ...currFilter, ...transformedRender, ...parentFilter };
        }
    };

    transformFilter(nestedSelectfilterInfo);

    return transformedFilterInfo;
};
export const getFormattedRangeValues = (value, bounds, isSuffix) => {
    const formattedValues = {};

    formattedValues.min = value.min || bounds?.minValue;
    formattedValues.max = value.max || bounds?.maxValue;

    if (isSuffix) {
        if (value.min === bounds?.maxValue || formattedValues.max === bounds?.maxValue) {
            formattedValues.suffix = PLUS_SUFFIX;
        }
    }

    return formattedValues;
};

export const getAppliedFiltersWithInfo = (parsedAppliedFilters, filtersInfo, filterOrder, appliedFilters) => {
    return filterOrder.reduce((acc, filterAttr) => {
        const currFilter = filtersInfo[filterAttr];
        const currAppliedValues = parsedAppliedFilters[filterAttr];
        let labels = [];

        if (currFilter) {
            const isMultiple = currFilter.render && currFilter.render.customConfiguration && currFilter.render.customConfiguration.selectionType === selectionType.MULTIPLE;

            if (currAppliedValues && Array.isArray(currAppliedValues)) {
                const valuesInfo = currFilter.render.customConfiguration.valuesInfo;
                const values = currFilter.render.customConfiguration.values;
                const selectTags = currAppliedValues.reduce((acc, curr) => {
                    const info = (valuesInfo && valuesInfo[curr]) || values.find(item => item.value === curr);

                    if (info) {
                        return [
                            ...acc,
                            {
                                filterId: filterAttr,
                                id: currFilter.id,
                                label: info.name,
                                props: { id: info.value, prefix: null, format: false, isRelaxed: appliedFilters?.[filterAttr]?.isRelaxed, is_hidden: currFilter.isHidden },
                                name: currFilter.name,
                                parentFilterAttr: currFilter.parentFilterAttr,
                                isMultiple
                            }
                        ];
                    }
                    return acc;
                }, []);

                labels = [...labels, ...selectTags];
            }
            else if (isObject(currAppliedValues)) {
                if (!(currAppliedValues.max === 0)) { // range filters - if min and max both equals zero then do not genrate a label
                    const connector = (currAppliedValues.min || currAppliedValues.max) ? { pos: 'B/W', symbol: '-' } : null;
                    const bounds = currFilter
                        && currFilter.render
                        && currFilter.render.customConfiguration
                        && currFilter.render.customConfiguration.range;

                    const formattedValues = getFormattedRangeValues(currAppliedValues, bounds, !ATTR_WITHOUT_SUFFIX[filterAttr]);

                    labels.push({
                        filterId: filterAttr,
                        id: currFilter.id,
                        isMultiple,
                        props: {
                            ...formattedValues,
                            prefix: `${currFilter.name}:`,
                            connector,
                            format: !ATTR_NOT_TO_FORMAT[filterAttr],
                            isRelaxed: appliedFilters?.[filterAttr]?.isRelaxed,
                            is_hidden: currFilter.isHidden
                        },
                        name: currFilter.name,
                        parentFilterAttr: currFilter.parentFilterAttr
                    });
                }
            }
        }
        return [...acc, ...labels];
    }, []);
};

export const generateContent = ({ label, locale, props: { suffix, prefix, connector, format, min, max } = {}}) => {
    let output = prefix ? `${prefix} ` : '';
    const isMin = isFinite(min);
    const isMax = isFinite(max);

    if (isMin || isMax) {
        let formatMin = '';
        let formatMax = '';

        if (isMin) {
            formatMin = format ? formatPrice(min, locale) : min;
        }
        if (isMax) {
            formatMax = format ? formatPrice(max, locale) : max;
            formatMax += (suffix || '');
        }

        if (isMin && isMax && min === max) {
            output += `${formatMin}${suffix || ''}`; // special case if max and min isEqual then only show one w/o connector
        }
        else if (connector && connector.pos && connector.symbol) {
            if (connector.pos === 'B/W') {
                output += `${formatMin} ${connector.symbol} ${formatMax}`;
            }
        }
    }
    else if (label) {
        output += label;
    }

    return output;
};

export const getAppliedRow = height => {
    if (!height || height === ZERO_ROW_HEIGHT) {
        return 0;
    }
    if (height <= ONE_ROW_HEIGHT) {
        return 1;
    }
    if (height > ONE_ROW_HEIGHT) {
        return 2;
    }
    return 0;
};

export const getModifiedVisualizationFilter = (type, { name, code }) => {
    switch (type) {
        case visualizationFilters.VIEW_TYPE:
            return {
                name,
                icon: viewTypeTanakConfig[code].icon,
                value: viewTypeTanakConfig[code].pwaCode
            };
        default:
            return { name, value: code };
    }
};

export const getVisualizationFilter = ({ label, sectionName, options }, type) => {
    // Expected Values --> VIEW_TYPE and SORT_TYPE
    const filterAttr = visualizationFilters[`${type.toUpperCase()}_TYPE`];

    return {
        [filterAttr]: {
            name: type,
            attribute: filterAttr,
            sectionName,
            isTagVisible: false,
            render: {
                customConfiguration: {
                    label,
                    values: options.map(({ name, code }) => ({ name, value: code })),
                    selectionType: 'SINGLE'
                },
                defaultComponent: {
                    componentType: 'select'
                }
            }
        }
    };
};

export const parseFiltersForTracking = filters => Object.entries(filters).reduce((prev, [key, value]) => {
    if (value.length === 1) {
        return { ...prev, [key]: value[0] };
    }
    return { ...prev, [key]: value };
}, {});

export const removeExcludedFilters = filtersConfig => (isEmpty(filtersConfig) ? null : filtersConfig.filter(filter => filter.id !== 'category' && filter.id !== 'location'));

const findParentBrandObj = (model, makeValues) => {
    return makeValues.find(({ nestedValues }) => {
        return nestedValues.some(val => val === model);
    });
};

export const getNestedFilters = (abundanceFilters, filtersByAttr) => {
    const nestedFilters = {};
    const abundanceModel = abundanceFilters.model || abundanceFilters.m_tipe;
    const abundanceMake = abundanceFilters.make;
    const isNestedFilter = (abundanceMake || abundanceModel)
        && (filtersByAttr && filtersByAttr.make && filtersByAttr.make.nestedFilterId);

    if (isNestedFilter) {
        const makeFilterValues = filtersByAttr.make.render.customConfiguration.values;
        const makeValuesFromQuery = abundanceMake ? abundanceMake.split(',') : [];
        const modelValuesFromQuery = abundanceModel ? abundanceModel.split(',') : [];

        nestedFilters.make = [];
        makeValuesFromQuery.forEach(make => {
            const makeObj = {};

            makeObj[make] = {};

            const values = makeFilterValues.filter(makeValue => makeValue.value.toLowerCase() === make.toLowerCase());

            if (values.length) {
                const { nestedValues } = values[0];
                const makeOptionsSet = new Set(nestedValues);

                modelValuesFromQuery.forEach((modelValue, idx) => {
                    if (makeOptionsSet.has(modelValue)) {
                        makeObj[make].model = makeObj[make].model || [];
                        makeObj[make].model.push(modelValue);
                        modelValuesFromQuery.splice(idx, 1);
                    }
                });
                nestedFilters.make.push(makeObj);
            }
        });

        if (modelValuesFromQuery.length) {
            modelValuesFromQuery.forEach(modelValue => {
                const makeObj = {};
                const parentBrandObj = findParentBrandObj(modelValue, makeFilterValues);
                const parentValue = parentBrandObj && parentBrandObj.value.toLowerCase();

                makeObj[parentValue] = makeObj[parentValue] || {};
                makeObj[parentValue].model = makeObj[parentValue].model || [];
                makeObj[parentValue].model.push(modelValue);

                nestedFilters.make.push(makeObj);
            });
        }
    }

    return nestedFilters;
};

export const createClubbedFilters = (appliedFilters = []) => {
    const clubbedFiltersMap = {};
    const modifiedFilters = (appliedFilters || []).map(f => {
        if (f.filterId === QUICK_FILTERS.MODEL) {
            return {
                ...f,
                filterId: QUICK_FILTERS.MAKE,
                name: QUICK_FILTERS.MAKE_AND_MODEL
            };
        }
        return f;
    });

    // Group filters by `filterId`
    modifiedFilters.forEach(filter => {
        const { filterId, id, name, filterPaneId } = filter;

        if (!clubbedFiltersMap[filterId]) {
            clubbedFiltersMap[filterId] = {
                filterId,
                filterPaneId,
                id,
                name,
                count: 0
            };
        }

        // Increment count for the grouped filter
        clubbedFiltersMap[filterId].count += 1;
    });

    return Object.values(clubbedFiltersMap);
};

export const hasMakeAndModel = (appliedFilters = []) => {
    // Check if we have both make and model filters in the appliedFilters
    const hasMake = appliedFilters?.some(filter => filter.filterId === 'make');
    const hasModel = appliedFilters?.some(filter => filter.filterId === 'model');

    return hasMake && hasModel;
};
